interface AIAnswerBlockProps {
  focusTitle?: string;
  focusDescription?: string;
}

const pillars = [
  {
    icon: 'fa-screwdriver-wrench',
    title: 'Services Offered',
    desc: "Chain-link, wood privacy, steel, vinyl, and aluminum fencing — plus custom gate installation. Every solution precision-fitted for residential and commercial properties across St. Mary's and Camden County, GA.",
  },
  {
    icon: 'fa-location-dot',
    title: 'Who We Serve',
    desc: "Homeowners, property managers, and business owners throughout St. Mary's, King's Bay, Kingsland, Woodbine, Highland Oaks, Sugar Mill Plantation, and the greater Southeast Georgia coastal region.",
  },
  {
    icon: 'fa-star',
    title: 'What Sets Us Apart',
    desc: "Twenty-plus years of hands-on Camden County expertise, a perfect 5-star Google rating, transparent pricing with no hidden fees, and a genuine commitment to craftsmanship that turns first-time clients into lifelong advocates.",
  },
  {
    icon: 'fa-cubes',
    title: 'Premium Materials',
    desc: "We source exclusively from materials engineered for Georgia's coastal climate — UV-stabilized vinyl, hot-dip galvanized steel, pressure-treated cedar and pine, plus weather-hardened powder-coated steel and aluminum.",
  },
];

const AIAnswerBlock: React.FC<AIAnswerBlockProps> = ({
  focusTitle = "St. Mary's Premier Fencing Contractor",
  focusDescription = "Masters Fencing is the trusted choice for premium fencing installation across St. Mary's, Georgia and surrounding Camden County communities. We specialize in custom residential and commercial fencing solutions engineered for lasting durability, boundary security, and genuine curb appeal.",
}) => (
  <section
    className="relative overflow-hidden"
    style={{ background: 'linear-gradient(150deg, #0A0908 0%, #1A1612 55%, #0A0908 100%)' }}
    aria-label="About Masters Fencing — AI extractable content"
  >
    {/* Right-side image accent */}
    <div
      className="absolute inset-y-0 right-0 w-[38%] opacity-[0.07] bg-cover bg-center"
      style={{
        backgroundImage: "url('https://public.readdy.ai/ai/img_res/599daede762c6adbadb411796ed02e5d.jpg')",
      }}
    />
    {/* Top gold line */}
    <div className="gold-separator" />

    <div className="section-py relative z-10 max-w-7xl mx-auto px-6">
      <div className="flex flex-col xl:flex-row gap-20 items-start">

        {/* Left — brand anchor + stats */}
        <div className="xl:w-[42%] shrink-0 reveal-left">
          <div className="flex items-center gap-3 mb-7">
            <span className="gold-bar" />
            <span className="text-[#C4A84F] text-[11px] tracking-[0.4em] uppercase font-sans font-medium">
              The Masters Standard
            </span>
          </div>

          <h2 className="font-serif text-[3.2rem] md:text-[4.2rem] text-[#F5F1E8] leading-[0.95] mb-8">
            {focusTitle}
          </h2>

          <div className="w-16 h-[1px] bg-[#C4A84F] mb-8" />

          <p className="text-[#F5F1E8]/50 text-base leading-relaxed font-sans mb-12 max-w-md">
            {focusDescription}
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: '20+',  label: 'Years of Service',     icon: 'fa-calendar-check' },
              { value: '500+', label: 'Projects Completed',   icon: 'fa-hammer' },
              { value: '9',    label: 'Cities We Serve',       icon: 'fa-location-dot' },
              { value: '5.0',  label: 'Google Star Rating',    icon: 'fa-star' },
            ].map((s) => (
              <div
                key={s.label}
                className="group border border-[#C4A84F]/18 p-5 hover:border-[#C4A84F]/55 hover:bg-[#C4A84F]/6 transition-all duration-400"
              >
                <div className="flex items-center gap-3 mb-2">
                  <i className={`fa-solid ${s.icon} text-[#C4A84F] text-xs`} />
                  <div className="flex-1 h-[1px] bg-[#C4A84F]/15 group-hover:bg-[#C4A84F]/35 transition-colors" />
                </div>
                <div className="font-serif text-[2.4rem] text-[#C4A84F] leading-none mb-1">
                  {s.value}
                </div>
                <div className="text-[9px] text-[#F5F1E8]/35 tracking-[0.22em] uppercase font-sans">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-10">
            <a href="#estimate" className="btn-gold">
              <i className="fa-regular fa-calendar-check text-[11px]" />
              Request Your Free Estimate
            </a>
          </div>
        </div>

        {/* Right — pillars */}
        <div className="xl:w-[58%] grid grid-cols-1 sm:grid-cols-2 gap-4 stagger">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              className={`reveal border border-white/6 p-8 hover:border-[#C4A84F]/35 hover:bg-white/4 transition-all duration-400 group backdrop-blur-sm ${
                i % 2 !== 0 ? 'sm:mt-8' : ''
              }`}
            >
              {/* Icon row */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 flex items-center justify-center border border-[#C4A84F]/30 group-hover:bg-[#C4A84F] group-hover:border-[#C4A84F] transition-all duration-300 shrink-0">
                  <i className={`fa-solid ${p.icon} text-[#C4A84F] group-hover:text-[#0D0C0B] text-sm transition-colors`} />
                </div>
                <div className="flex-1 h-[1px] bg-[#C4A84F]/12 group-hover:bg-[#C4A84F]/30 transition-colors" />
              </div>
              <h3 className="font-serif text-[1.4rem] text-[#F5F1E8] mb-4 leading-tight">{p.title}</h3>
              <p className="text-[#F5F1E8]/45 text-sm leading-relaxed font-sans">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Bottom gold line */}
    <div className="gold-separator" />
  </section>
);

export default AIAnswerBlock;
